/**
 * Pagina /products/:id — dettaglio prodotto con tab.
 *
 * Tab disponibili:
 *  - Anagrafica e Release (anagrafica prodotto + lista release/SBOM, unificate; in fondo lo
 *    storico audit di tutte le release del prodotto)
 *  - Mapping (CPE/PURL + import CSV)
 *  - Destinatari notifiche
 *  - Forniture (consegne a clienti — Slice B)
 *  - Notifiche vulnerabilità (impatto + invii manuali — Slice C)
 */

import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useProduct } from './hooks.js';
import { ReleaseSection } from './ReleaseSection.js';
import { MappingSection } from './MappingSection.js';
import { OwnershipSection } from './OwnershipSection.js';
import { DeploymentsSection } from './DeploymentsSection.js';
import { NotificationsSection } from './NotificationsSection.js';
import { ReleaseAuditTrail } from './ReleaseAuditTrail.js';
import styles from './Products.module.css';

type Tab = 'anagrafica' | 'mapping' | 'ownership' | 'forniture' | 'notifiche';

/**
 * Tab del dettaglio prodotto. `slug` è il frammento persistito nella URL
 * (`/products/<id>#<slug>`) così la navigazione è condivisibile e sopravvive al refresh.
 * Anagrafica e Release sono unificate in un'unica tab.
 */
const TABS = [
  { id: 'anagrafica', label: 'Anagrafica e Release', slug: 'anagrafica' },
  { id: 'mapping', label: 'Mapping ID', slug: 'mapping-id' },
  { id: 'ownership', label: 'Destinatari notifiche', slug: 'destinatari-notifiche' },
  { id: 'forniture', label: 'Forniture', slug: 'forniture' },
  { id: 'notifiche', label: 'Notifiche vulnerabilità', slug: 'notifiche' },
] satisfies { id: Tab; label: string; slug: string }[];

/**
 * Ricava la tab attiva dall'hash della URL; fallback ad 'anagrafica' se assente/sconosciuto.
 * Il vecchio slug `release` rimanda alla tab unificata (link condivisibili retrocompatibili).
 */
function tabFromHash(hash: string): Tab {
  const slug = hash.replace(/^#/, '');
  if (slug === 'release') return 'anagrafica';
  return TABS.find((t) => t.slug === slug)?.id ?? 'anagrafica';
}

/** Pannello anagrafica del prodotto. */
function Anagrafica({ productId }: { productId: string }) {
  const { data: prodotto, isLoading, isError, error } = useProduct(productId);

  if (isLoading) {
    return (
      <p className={styles.loadingMsg} data-testid="loading-prodotto">
        Caricamento prodotto…
      </p>
    );
  }

  if (isError) {
    return (
      <p className={styles.errorMsg} data-testid="errore-prodotto">
        {error instanceof Error ? error.message : 'Errore nel caricamento del prodotto'}
      </p>
    );
  }

  if (!prodotto) return null;

  return (
    <div className={styles.anagrafica} data-testid="anagrafica-prodotto">
      <div className={styles.anagraficaItem} data-testid="anagrafica-item-internalId">
        <span className={styles.anagraficaLabel} data-testid="anagrafica-label-internalId">
          ID interno
        </span>
        <span className={styles.anagraficaValue} data-testid="anagrafica-value-internalId">
          {prodotto.internalId}
        </span>
      </div>
      <div className={styles.anagraficaItem} data-testid="anagrafica-item-nome">
        <span className={styles.anagraficaLabel} data-testid="anagrafica-label-nome">
          Nome
        </span>
        <span className={styles.anagraficaValue} data-testid="anagrafica-value-nome">
          {prodotto.name}
        </span>
      </div>
      {prodotto.description && (
        <div className={styles.anagraficaItem} data-testid="anagrafica-item-descrizione">
          <span className={styles.anagraficaLabel} data-testid="anagrafica-label-descrizione">
            Descrizione
          </span>
          <span className={styles.anagraficaValue} data-testid="anagrafica-value-descrizione">
            {prodotto.description}
          </span>
        </div>
      )}
      {prodotto.notes && (
        <div className={styles.anagraficaItem} data-testid="anagrafica-item-note">
          <span className={styles.anagraficaLabel} data-testid="anagrafica-label-note">
            Note
          </span>
          <span className={styles.anagraficaValue} data-testid="anagrafica-notes">
            {prodotto.notes}
          </span>
        </div>
      )}
      {prodotto.cpePrefix && (
        <div className={styles.anagraficaItem} data-testid="anagrafica-item-cpePrefix">
          <span className={styles.anagraficaLabel} data-testid="anagrafica-label-cpePrefix">
            CPE prefix
          </span>
          <span className={styles.anagraficaValue} data-testid="anagrafica-value-cpePrefix">
            {prodotto.cpePrefix}
          </span>
        </div>
      )}
      {prodotto.purl && (
        <div className={styles.anagraficaItem} data-testid="anagrafica-item-purl">
          <span className={styles.anagraficaLabel} data-testid="anagrafica-label-purl">
            PURL
          </span>
          <span className={styles.anagraficaValue} data-testid="anagrafica-value-purl">
            {prodotto.purl}
          </span>
        </div>
      )}
      {/* DT Project UUID nascosto: dettaglio interno DT, non rilevante per l'utente del portale. */}
    </div>
  );
}

/** Pagina dettaglio prodotto con navigazione a tab. */
export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // La tab attiva è derivata dall'hash: unica fonte di verità, niente stato duplicato.
  const tabAttiva = tabFromHash(location.hash);

  // id è garantito dalla route, ma lo gestiamo difensivamente
  if (!id) {
    return (
      <p className={styles.errorMsg} data-testid="errore-id-mancante">
        ID prodotto mancante nella URL.
      </p>
    );
  }

  return (
    <div data-testid="product-detail-page">
      <div className={styles.pageHeader}>
        <button
          className={styles.linkCell}
          onClick={() => navigate('/products')}
          data-testid="btn-torna-lista"
        >
          ← Prodotti
        </button>
        <h1 className={styles.pageTitle}>{id}</h1>
      </div>

      {/* Tab navigation — il click aggiorna l'hash della URL (replace: niente spam nella history) */}
      <div className={styles.tabs} role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={tabAttiva === tab.id}
            className={`${styles.tab} ${tabAttiva === tab.id ? styles.tabActive : ''}`}
            onClick={() => navigate(`${location.pathname}#${tab.slug}`, { replace: true })}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenuto tab attiva */}
      <div role="tabpanel" data-testid={`contenuto-tab-${tabAttiva}`}>
        {tabAttiva === 'anagrafica' && (
          <>
            <Anagrafica productId={id} />
            <ReleaseSection productId={id} />
            {/* In fondo alla pagina (sopra la guida): storico di tutte le release */}
            <ReleaseAuditTrail productId={id} />
          </>
        )}
        {tabAttiva === 'mapping' && <MappingSection productId={id} />}
        {tabAttiva === 'ownership' && <OwnershipSection productId={id} />}
        {tabAttiva === 'forniture' && <DeploymentsSection productId={id} />}
        {tabAttiva === 'notifiche' && <NotificationsSection productId={id} />}
      </div>
    </div>
  );
}
